import type { SelectedUser } from "Components/Chat/ChatSelectedMembers";
import { useCanInitiateChatWith } from "Hooks/Chat/useCanInitiateChatWith";

/** Why a person cannot be picked for a conversation — see {@link useSelectedUserIneligibility}. */
export type SelectedUserIneligibility =
  | "customerWritesFirst"
  | "notFullMoone"
  | "noHandle";

/**
 * The one place that decides whether a person cannot be picked for a conversation — picked
 * already, or still a search result — and why. Returns the question as a function because the
 * answer depends on whom it is asked about; the answer is the reason, or undefined for an
 * eligible person, so callers can ask "whether" and "why" of the same call.
 *
 * Three rules today: a business persona cannot bring a customer in (App ADR-0015), a
 * verified-only conversation admits Full Moone personas only (App ADR-0012), and nobody without
 * a handle is brought into a new conversation (Social ADR-0004: the handle is the chat contract).
 * An unknown tier or handle (a counterpart taken from a Stream channel) passes, the server
 * decides. A new rule is added here and every picker follows.
 */
export const useSelectedUserIneligibility = (isFullMooneRequired = false) => {
  const canInitiateChatWith = useCanInitiateChatWith();

  return ({
    accessRoleCode,
    isFullMoone,
    handle,
  }: Pick<SelectedUser, "accessRoleCode" | "isFullMoone" | "handle">):
    | SelectedUserIneligibility
    | undefined => {
    if (!canInitiateChatWith(accessRoleCode)) {
      return "customerWritesFirst";
    }

    if (handle === null) {
      return "noHandle";
    }

    if (isFullMooneRequired && isFullMoone === false) {
      return "notFullMoone";
    }

    return undefined;
  };
};
